import React, { useCallback, useMemo } from "react";
import { addDays, subDays } from "date-fns";
import { useStatistics } from "../../../../hooks/useStatistics";
import { TrendCard } from "../../../../components/card/TrendCard";
import { useTranslation } from "react-i18next";
import { useIsFetching } from "@tanstack/react-query";
import type { Query } from "@tanstack/react-query";

// Right now we decided to go with week from Monday to Monday
const formatUtcStart = (date: Date) => {
	const utcDate = new Date(
		Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
	);
	return utcDate.toISOString();
};

const formatUtcEnd = (date: Date) => {
	const utcDate = new Date(
		Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)
	);
	return utcDate.toISOString();
};

const createRanges = () => ({
	LAST_7_VS_PREV_7: {
		titleKey: "PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_7_VS_PREV_7",
		getDates: () => {
			const now = new Date();
			return {
				firstRangeStartDate: formatUtcStart(subDays(now, 14)),
				firstRangeEndDate: formatUtcEnd(subDays(now, 7)),
				lastRangeStartDate: formatUtcStart(subDays(now, 7)),
				lastRangeEndDate: formatUtcEnd(now),
			};
		},
	},
	LAST_7_VS_LAST_30: {
		titleKey: "PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_7_VS_LAST_30",
		getDates: () => {
			const now = new Date();
			return {
				firstRangeStartDate: formatUtcStart(subDays(now, 30)),
				firstRangeEndDate: formatUtcEnd(now),
				lastRangeStartDate: formatUtcStart(subDays(now, 7)),
				lastRangeEndDate: formatUtcEnd(now),
			};
		},
	},
	LAST_7_VS_LAST_90: {
		titleKey: "PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_7_VS_LAST_90",
		getDates: () => {
			const now = new Date();
			return {
				firstRangeStartDate: formatUtcStart(subDays(now, 90)),
				firstRangeEndDate: formatUtcEnd(now),
				lastRangeStartDate: formatUtcStart(subDays(now, 7)),
				lastRangeEndDate: formatUtcEnd(now),
			};
		},
	},
	PROGRESS_SINCE_START: {
		titleKey: "PARTICIPANT.BIOMETRICS.TREND_CARD.PROGRESS_SINCE_START",
		getDates: (firstBiometric?: string) => {
			const now = new Date();
			const firstBiometricDate = firstBiometric ? new Date(firstBiometric) : now;
			return {
				firstRangeStartDate: formatUtcStart(firstBiometricDate),
				firstRangeEndDate: formatUtcEnd(addDays(firstBiometricDate, 30)),
				lastRangeStartDate: formatUtcStart(subDays(now, 7)),
				lastRangeEndDate: formatUtcEnd(now),
			};
		},
	},
});

const TrendCardWrapper: React.FC<{
	participantId: number;
	type: string;
	titleKey: string;
	getDates: (firstBiometric?: string) => {
		firstRangeStartDate: string;
		firstRangeEndDate: string;
		lastRangeStartDate: string;
		lastRangeEndDate: string;
	} | null;
	firstBiometric?: string;
}> = ({ participantId, type, titleKey, getDates, firstBiometric }) => {
	const { t } = useTranslation("common");
	const title = t(titleKey);

	const dates = useMemo(() => getDates(firstBiometric), [getDates, firstBiometric]);

	const { data, isLoading, error } = useStatistics({
		participantId,
		firstRangeStartDate: dates?.firstRangeStartDate || "",
		firstRangeEndDate: dates?.firstRangeEndDate || "",
		lastRangeStartDate: dates?.lastRangeStartDate || "",
		lastRangeEndDate: dates?.lastRangeEndDate || "",
		type,
	});

	if (!dates) {
		return (
			<p>
				{t("PARTICIPANT.BIOMETRICS.TREND_CARD.NO_DATA", {
					value: title,
				})}
			</p>
		);
	}

	if (isLoading) return null;
	if (error) return <p>{t("PARTICIPANT.BIOMETRICS.MESSAGES.ERROR")}</p>;
	if (!data) return <p>{t("PARTICIPANT.BIOMETRICS.MESSAGES.NO_DATA")}</p>;

	const getLabels = () => {
		switch (titleKey) {
			case "PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_7_VS_PREV_7":
				return {
					period1Label: t("PARTICIPANT.BIOMETRICS.TREND_CARD.PREVIOUS_7_DAYS"),
					period2Label: t("PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_7_DAYS"),
				};
			case "PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_7_VS_LAST_30":
				return {
					period1Label: t("PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_30_DAYS"),
					period2Label: t("PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_7_DAYS"),
				};
			case "PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_7_VS_LAST_90":
				return {
					period1Label: t("PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_90_DAYS"),
					period2Label: t("PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_7_DAYS"),
				};
			case "PARTICIPANT.BIOMETRICS.TREND_CARD.PROGRESS_SINCE_START":
				return {
					period1Label: t("PARTICIPANT.BIOMETRICS.TREND_CARD.FIRST_30_DAYS"),
					period2Label: t("PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_7_DAYS"),
				};
			default:
				return {
					period1Label: t("PARTICIPANT.BIOMETRICS.TREND_CARD.FIRST_RANGE"),
					period2Label: t("PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_RANGE"),
				};
		}
	};

	const getTrendInfo = (description: string) => {
		if (description === "NO_DATA") {
			return {
				description: t("PARTICIPANT.BIOMETRICS.TREND_CARD.NO_DATA_TREND"),
				descriptionColor: null,
			};
		}

		const parts = description.split("_");

		// For BLOOD_PRESSURE, we expect a specific format
		if (type === "BLOOD_PRESSURE") {
			// Systolic
			const systolicTrend = parts[3].toLowerCase();
			const systolicValue = parts[4];
			const systolicColor =
				systolicTrend === "increased" ? "trend-text--red" : "trend-text--green";
			const systolicArrow = systolicTrend === "increased" ? "↑" : "↓";

			// Diastolic
			const diastolicTrend = parts[6].toLowerCase();
			const diastolicValue = parts[7];
			const diastolicColor =
				diastolicTrend === "increased" ? "trend-text--red" : "trend-text--green";
			const diastolicArrow = diastolicTrend === "increased" ? "↑" : "↓";

			return {
				description: (
					<div>
						<span className={systolicColor}>
							{systolicArrow} {Math.abs(Number(systolicValue))}{" "}
							{t("PARTICIPANT.BIOMETRICS.TREND_CARD.PTS_SYSTOLIC")}
						</span>
						{" / "}
						<span className={diastolicColor}>
							{diastolicArrow} {Math.abs(Number(diastolicValue))}{" "}
							{t("PARTICIPANT.BIOMETRICS.TREND_CARD.PTS_DIASTOLIC")}
						</span>
					</div>
				),
			};
		}

		if (type.startsWith("GLUCOSE")) {
			const trend = parts[parts.length - 2].toLowerCase();
			const value = parseFloat(parts[parts.length - 1]).toFixed(2);
			const trendColor = trend === "increased" ? "trend-text--red" : "trend-text--green";
			const trendArrow = trend === "increased" ? "↑" : "↓";

			return {
				description: (
					<div>
						<span className={trendColor}>
							{trendArrow} {value}{" "}
							{t("PARTICIPANT.BIOMETRICS.TREND_CARD.PTS_GLUCOSE")}
						</span>
					</div>
				),
			};
		}

		// WEIGHT
		const trend = parts[1].toLowerCase();
		const value = parseFloat(parts[2]).toFixed(2);
		const trendColor = trend === "increased" ? "trend-text--red" : "trend-text--green";
		const trendCounter = trend === "increased" ? "+" : "";

		let timePeriodKey;
		switch (titleKey) {
			case "PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_7_VS_PREV_7":
				timePeriodKey = "PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_14_DAYS";
				break;
			case "PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_7_VS_LAST_30":
				timePeriodKey = "PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_30_DAYS";
				break;
			case "PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_7_VS_LAST_90":
				timePeriodKey = "PARTICIPANT.BIOMETRICS.TREND_CARD.LAST_90_DAYS";
				break;
			case "PARTICIPANT.BIOMETRICS.TREND_CARD.PROGRESS_SINCE_START":
				timePeriodKey = "PARTICIPANT.BIOMETRICS.TREND_CARD.SINCE_START";
				break;
			default:
				timePeriodKey = "PARTICIPANT.BIOMETRICS.TREND_CARD.PTS_WEIGHT_WEEK";
		}

		return {
			description: (
				<div>
					<span className={trendColor}>
						{t("PARTICIPANT.BIOMETRICS.TREND_CARD.RATE_CHANGE")}{" "}
						{t(timePeriodKey).toLowerCase()}: {trendCounter}
						{value} {t("PARTICIPANT.BIOMETRICS.TREND_CARD.PTS_WEIGHT_WEEK")}
					</span>
				</div>
			),
		};
	};

	const getLimitedData = (limitedData: string) => {
		if (limitedData === "NORMAL") return null;

		const lastUnderscore = limitedData.lastIndexOf("_");
		if (lastUnderscore === -1) return null;

		const value = limitedData.slice(lastUnderscore + 1);
		const readingText =
			value === "1"
				? t("PARTICIPANT.BIOMETRICS.TREND_CARD.READING")
				: t("PARTICIPANT.BIOMETRICS.TREND_CARD.READINGS");

		const limitedDataText = t("PARTICIPANT.BIOMETRICS.TREND_CARD.LIMITED_DATA");
		return limitedDataText.replace("{{count}}", value).replace("{{reading}}", readingText);
	};

	const { period1Label, period2Label } = getLabels();
	const { description, descriptionColor } = getTrendInfo(data.description);
	const limitedData = getLimitedData(data.usesLimitedData);

	return (
		<TrendCard
			title={title}
			period1={{
				label: period1Label,
				averageSeverity: data.firstRangeStatistics.averageSeverity,
				avg:
					data.firstRangeStatistics.average === 0
						? "-"
						: data.firstRangeStatistics.average,
				min: data.firstRangeStatistics.min,
				max: data.firstRangeStatistics.max,
				readings: data.firstRangeStatistics.numberOfReadings,
				type: type,
			}}
			period2={{
				label: period2Label,
				averageSeverity: data.lastRangeStatistics.averageSeverity,
				avg:
					typeof data.lastRangeStatistics.average === "number" &&
					data.lastRangeStatistics.average === 0
						? "-"
						: data.lastRangeStatistics.average,
				min: data.lastRangeStatistics.min,
				max: data.lastRangeStatistics.max,
				readings: data.lastRangeStatistics.numberOfReadings,
				type: type,
			}}
			description={description}
			descriptionColor={descriptionColor ?? undefined}
			limitedData={limitedData ?? undefined}
		/>
	);
};

export const BiometricsTrends: React.FC<{
	participantId: number;
	type: string;
	firstBiometric: string;
}> = ({ participantId, type, firstBiometric }) => {
	const ranges = useMemo(() => Object.values(createRanges()), []);
	const { t } = useTranslation("common");

	const fetchingStatistics = useCallback(
		(query: Query) => {
			const key = query.queryKey;
			return (
				Array.isArray(key) &&
				key[0] === "statistics" &&
				key[1]?.participantId === participantId
			);
		},
		[participantId]
	);

	const isFetching = useIsFetching({ predicate: fetchingStatistics });

	return (
		<div className="row g-3 mt-3 mb-4">
			{/* Loading */}
			{isFetching > 0 && (
				<div className="d-flex justify-content-center align-items-center">
					<div className="spinner-border text-primary" />
					<span className="ms-2">
						{t("PARTICIPANT.BIOMETRICS.MESSAGES.TRENDS_LOADING")}
					</span>
				</div>
			)}

			{ranges.map(({ titleKey, getDates }) => {
				return (
					<div
						key={titleKey}
						className="col-md-6"
					>
						<TrendCardWrapper
							key={titleKey}
							participantId={participantId}
							type={type}
							titleKey={titleKey}
							getDates={getDates}
							firstBiometric={firstBiometric}
						/>
					</div>
				);
			})}
		</div>
	);
};
